import { useState, useMemo, useEffect } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { ChevronLeft, ChevronRight, Loader2, Check, Car, Receipt, ClipboardCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WizardState {
    make: string;
    model: string;
    color: string;
    vin: string;
    year: string;
    engineSize: string;
    regNumber: string;
    transactionId: number | null;
}

const INITIAL_STATE: WizardState = {
    make: '',
    model: '',
    color: '',
    vin: '',
    year: '',
    engineSize: '',
    regNumber: '',
    transactionId: null,
};

const STEPS = [
    { key: 'details', label: 'Vehicle Details', icon: Car, description: 'Make, model & specs' },
    { key: 'transaction', label: 'Transaction', icon: Receipt, description: 'Payment record' },
    { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Confirm & create' },
];

interface CreateVehicleWizardProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (vehicleId: number) => void;
}

// ─── Step Components ─────────────────────────────────────────────────────────

function VehicleDetailsStep({
    state,
    updateState,
}: {
    state: WizardState;
    updateState: (updates: Partial<WizardState>) => void;
}) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Make *</Label>
                    <Input value={state.make} onChange={e => updateState({ make: e.target.value })} placeholder="Toyota" />
                </div>
                <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={state.model} onChange={e => updateState({ model: e.target.value })} placeholder="Vitz" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Color *</Label>
                    <Input value={state.color} onChange={e => updateState({ color: e.target.value })} placeholder="Silver" />
                </div>
                <div className="space-y-2">
                    <Label>Year *</Label>
                    <Input type="number" value={state.year} onChange={e => updateState({ year: e.target.value })} placeholder="2020" />
                </div>
            </div>

            <div className="space-y-2">
                <Label>VIN *</Label>
                <Input value={state.vin} onChange={e => updateState({ vin: e.target.value })} placeholder="Vehicle identification number" />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Engine Size</Label>
                    <Input type="number" step="0.1" value={state.engineSize} onChange={e => updateState({ engineSize: e.target.value })} placeholder="1.3" />
                </div>
                <div className="space-y-2">
                    <Label>Registration Number</Label>
                    <Input value={state.regNumber} onChange={e => updateState({ regNumber: e.target.value })} placeholder="GR 1234 56" />
                </div>
            </div>
        </div>
    );
}

function TransactionStep({
    transactionId,
    setTransactionId,
}: {
    transactionId: number | null;
    setTransactionId: (id: number | null) => void;
}) {
    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Select an existing debit transaction or create a new one to associate with this vehicle.
            </p>
            <TransactionSelector
                value={transactionId}
                onChange={(id) => setTransactionId(id)}
                defaultType="DEBIT"
                placeholder="Select or create a debit transaction..."
            />
        </div>
    );
}

function ReviewStep({ state }: { state: WizardState }) {
    const items = [
        { label: 'Make', value: state.make },
        { label: 'Model', value: state.model },
        { label: 'Color', value: state.color },
        { label: 'Year', value: state.year },
        { label: 'VIN', value: state.vin },
        { label: 'Engine Size', value: state.engineSize || '—' },
        { label: 'Registration', value: state.regNumber || '—' },
        { label: 'Transaction', value: state.transactionId ? `#${state.transactionId}` : '—' },
    ];

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Review the vehicle details before creating.</p>
            <div className="rounded-xl border bg-muted/20 divide-y">
                {items.map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-center px-4 py-3">
                        <span className="text-sm text-muted-foreground">{label}</span>
                        <span className="text-sm font-medium">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Wizard ──────────────────────────────────────────────────────────────────

export default function CreateVehicleWizard({
    open,
    onOpenChange,
    onSuccess,
}: CreateVehicleWizardProps) {
    const queryClient = useQueryClient();
    const [step, setStep] = useState(0);
    const [state, setState] = useState<WizardState>(INITIAL_STATE);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [direction, setDirection] = useState(0);

    useEffect(() => {
        if (open) {
            setState(INITIAL_STATE);
            setStep(0);
        }
    }, [open]);

    const isDirty = useMemo(() => {
        return (
            state.make !== '' ||
            state.model !== '' ||
            state.vin !== '' ||
            state.transactionId !== null
        );
    }, [state]);

    const updateState = (updates: Partial<WizardState>) => {
        setState(prev => ({ ...prev, ...updates }));
    };

    const canGoNext = (): boolean => {
        switch (step) {
            case 0: // Details
                return !!(state.make.trim() && state.model.trim() && state.color.trim() && state.vin.trim() && state.year);
            case 1: // Transaction
                return !!state.transactionId;
            case 2: // Review
                return true;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setDirection(1);
            setStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setDirection(-1);
            setStep(prev => prev - 1);
        }
    };

    const handleClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            resetAndClose();
        }
    };

    const resetAndClose = () => {
        setStep(0);
        setState(INITIAL_STATE);
        setShowCloseConfirm(false);
        onOpenChange(false);
    };

    const mutation = useMutation({
        mutationFn: async () => {
            if (!state.transactionId) throw new Error('Transaction is required');

            let registrationId: number | null = null;

            // Create registration first if provided
            if (state.regNumber.trim()) {
                const { data: reg, error: rError } = await supabase
                    .from('vehicle_registrations')
                    .insert({
                        number: state.regNumber.trim(),
                        type: 'registered',
                        transaction_id: state.transactionId,
                    })
                    .select('id')
                    .single();
                if (rError) throw rError;
                registrationId = reg.id;
            }

            // Create vehicle
            const { data: vehicle, error: vError } = await supabase
                .from('vehicles')
                .insert({
                    make: state.make.trim(),
                    model: state.model.trim(),
                    color: state.color.trim(),
                    vin: state.vin.trim(),
                    manufactured_at: parseInt(state.year),
                    engine_size: state.engineSize ? parseFloat(state.engineSize) : 0,
                    partner_id: null,
                    registration_id: registrationId,
                })
                .select('id')
                .single();
            if (vError) throw vError;

            // Link transaction via vehicle_transactions
            const { error: vtError } = await supabase
                .from('vehicle_transactions')
                .insert({
                    vehicle_id: vehicle.id,
                    transaction_type: 'landed_cost',
                    transaction_id: state.transactionId,
                });
            if (vtError) {
                // Rollback vehicle creation
                await supabase.from('vehicles').delete().eq('id', vehicle.id);
                throw vtError;
            }

            return vehicle.id as number;
        },
        onSuccess: (vehicleId) => {
            toast.success('Vehicle created');
            queryClient.invalidateQueries({ queryKey: ['unassigned-vehicles-for-partner'] });
            resetAndClose();
            onSuccess?.(vehicleId);
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(`Failed to create vehicle: ${error.message}`);
        },
    });

    const isSubmitting = mutation.isPending;

    const variants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 50 : -50,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (direction: number) => ({
            x: direction < 0 ? 50 : -50,
            opacity: 0,
        }),
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return <VehicleDetailsStep state={state} updateState={updateState} />;
            case 1:
                return (
                    <TransactionStep
                        transactionId={state.transactionId}
                        setTransactionId={(id) => updateState({ transactionId: id })}
                    />
                );
            case 2:
                return <ReviewStep state={state} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[70vw] !max-w-3xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar - Progress */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">Create Vehicle</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Add a new vehicle to the fleet.
                            </p>
                        </div>

                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isCompleted = i < step;

                                return (
                                    <div
                                        key={s.key}
                                        className={cn(
                                            "group flex items-center gap-3 p-3 rounded-xl transition-all duration-300",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                            isCompleted ? "opacity-70" : "opacity-50",
                                            isActive && "!opacity-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                            isActive ? "bg-primary text-primary-foreground" :
                                                isCompleted ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
                                        )}>
                                            {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                                {s.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                            onClick={handleClose}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel & Close
                        </Button>
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">Step {step + 1} of {STEPS.length}</h2>
                                <p className="text-xs text-muted-foreground">{STEPS[step].label}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <AnimatePresence mode="wait" initial={false} custom={direction}>
                                <motion.div
                                    key={step}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="h-full max-w-2xl mx-auto"
                                >
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                        <p className="text-muted-foreground">{STEPS[step].description}</p>
                                    </div>

                                    {renderStep()}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={step === 0 || isSubmitting}
                                    className="w-24 rounded-full"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Back
                                </Button>

                                {step < STEPS.length - 1 ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canGoNext()}
                                        className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => mutation.mutate()}
                                        disabled={isSubmitting}
                                        className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Check className="h-4 w-4 mr-2" />
                                        )}
                                        Create Vehicle
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
